import qs from "query-string";
import i18next from "src/i18n/config";
import { eventVersionService } from "src/services/eventVersionService/eventVersionService";
import RouteService from "src/services/routeService";
import { transformErrorResponse } from "src/store/helpers";
import { ListResponse } from "src/types";
import { EventReport } from "src/ui/containers/dashboard/devices/types";

import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";

type ParamsWithGroups<T> = T & {
    groupIds: number[];
    allWorkspaceGroupsLength: number;
};

type ParamsWithoutGroups<T> = T & {
    groupIds?: never;
    allWorkspaceGroupsLength?: never;
};

function normalizeGroupIds<T>({ allWorkspaceGroupsLength, groupIds, ...rest }: ParamsWithGroups<T> | ParamsWithoutGroups<T>) {
    if (!allWorkspaceGroupsLength || !groupIds) {
        return { allWorkspaceGroupsLength, groupIds, ...rest };
    }

    let groupIdsToUse = undefined;
    if (groupIds.length && groupIds.length !== allWorkspaceGroupsLength) {
        groupIdsToUse = groupIds;
    }

    return { groupIds: groupIdsToUse, ...rest };
}

type EventBuilder = EndpointBuilder<BaseQueryFn, "Events" | "ActivityReport", "eventsApi">;
type Category = "build" | "device" | "file" | "gateway" | "group" | "license" | "product" | "provisioning" | "user" | "workspace";

type GetEventsParamsBase = {
    workspaceId?: number;
    entityId?: string;
    category?: Category;
    level?: API.EventLevel;
    createdFrom?: string;
    createdTo?: string;
    pageIndex?: number;
    pageSize?: number;
};

export type GetEventsParams = ParamsWithGroups<GetEventsParamsBase> | ParamsWithoutGroups<GetEventsParamsBase>;

function getEvents(builder: EventBuilder) {
    return builder.query<ListResponse<EventReport>, GetEventsParams>({
        providesTags: ["Events"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.events.failedToFetchEvents")),
        query: async (params) => {
            const normalizedParams = normalizeGroupIds(params);
            const base = await RouteService.getEventsRoute();
            return {
                url: base + "events",
                params: normalizedParams,
                paramsSerializer: () => qs.stringify(normalizedParams, { arrayFormat: "index" }),
            };
        },
        transformResponse: (response: ListResponse<API.EventReport>) => {
            return {
                ...response,
                list: eventVersionService.normalizeList(response.list),
            };
        },
    });
}

type GetActivityReportParamsBase = {
    workspaceId: number;
    fromDate?: string;
    eventType?: API.EventLevel[];
};
type GetActivityReportParams = ParamsWithGroups<GetActivityReportParamsBase> | ParamsWithoutGroups<GetActivityReportParamsBase>;

function getActivityReport(builder: EventBuilder) {
    return builder.query<ListResponse<API.EventActivityReport>, GetActivityReportParams>({
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.events.failedToFetchEventsActivityReport")),
        providesTags: ["ActivityReport"],
        query: async (params) => {
            const normalizedParams = normalizeGroupIds(params);
            const base = await RouteService.getEventsRoute();
            return {
                url: base + "reports/activity",
                params: normalizedParams,
                paramsSerializer: () => qs.stringify(normalizedParams, { arrayFormat: "index" }),
            };
        },
    });
}

export default {
    getActivityReport,
    getEvents,
};
